import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, MaskLine, SectionLabel } from "@/components/Reveal";

const chapters = [
  { n: "01", title: "Who We Are", text: "Genspire is a professional staffing and recruitment partner helping organizations build capable teams while supporting professionals in finding roles that align with their skills, experience, and aspirations." },
  { n: "02", title: "Partner in Progress", text: "Progress works both ways. Businesses grow with the right people, and professionals grow with the right opportunities. We create connections where both can move forward with confidence." },
  { n: "03", title: "Our Approach to Staffing", text: "We start by listening and understanding. We take the time to understand business requirements, role expectations, workplace dynamics, candidate capabilities, and career goals before making the right connection." },
  { n: "04", title: "Understanding First", text: "Every requirement and every individual is different. Our approach is personalized, thoughtful, and focused on finding the right fit rather than simply filling a position." },
  { n: "05", title: "A Commitment to Quality", text: "We believe quality comes from relevance, transparency, attention to detail, and consistent communication. We remain committed to creating a professional and reliable experience for everyone involved." },
  { n: "06", title: "Built for Long-Term Relationships", text: "We don't see recruitment as a one-time transaction. We aim to build lasting relationships with clients, candidates, and partners through trust, responsiveness, consistency, and genuine understanding." },
  { n: "07", title: "Our Vision", text: "To be a trusted staffing partner where organizations find the talent they need, professionals find opportunities where they can grow, and every connection creates lasting value." },
];

export default function About() {
  return (
    <>
      <section className="pt-[128px] pb-14 lg:pt-[156px] lg:pb-20 relative overflow-hidden" data-testid="about-hero">
        <div className="pointer-events-none absolute -top-20 -left-32 h-[380px] w-[380px] rounded-full bg-brand-red/[0.05] blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <MaskLine delay={0.05}>
            <span className="flex items-center gap-4">
              <span className="h-px w-12 bg-brand-red" />
              <span className="font-mono text-[11px] uppercase tracking-[0.38em] text-brand-red font-medium">About Genspire</span>
            </span>
          </MaskLine>
          <h1 className="mt-7 font-display font-extrabold tracking-tight text-slate-900 text-4xl sm:text-6xl lg:text-7xl leading-[1.04] max-w-4xl">
            <MaskLine delay={0.18}>A staffing partner built</MaskLine>
            <MaskLine delay={0.3}>around people and <span className="text-brand-red">progress.</span></MaskLine>
          </h1>
          <MaskLine delay={0.44} className="mt-8 max-w-2xl">
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
              At Genspire, we believe meaningful growth begins with the right connection. We bring together organizations seeking the right talent and professionals seeking opportunities that align with their skills, aspirations, and ambitions. By understanding both sides, we create thoughtful, relevant connections that go beyond filling roles — building relationships that deliver value, trust, and progress over the long term.
            </p>
          </MaskLine>
        </div>
      </section>

      <section className="pb-16 lg:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 lg:sticky lg:top-28 self-start">
            <Reveal>
              <div className="border-l-2 border-brand-red pl-8 py-2">
                <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-sm">
                  Seven principles guide every engagement — from the first brief to long-term partnership.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="mt-10 hidden lg:flex items-center gap-5">
                <span className="block h-px w-24 bg-slate-200" />
                <span className="inline-block h-2 w-2 rotate-45 bg-brand-red/70" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal>
              <SectionLabel>Our Story</SectionLabel>
            </Reveal>
            <div className="space-y-0 border-t border-slate-200">
              {chapters.map((c, i) => (
                <Reveal key={c.n} delay={i * 0.04}>
                  <div className="group py-8 border-b border-slate-200 transition-colors duration-500 hover:bg-brand-light/40 px-4 -mx-4 rounded-lg" data-testid={`about-chapter-${c.n}`}>
                    <div className="flex items-baseline gap-5">
                      <span className="font-mono text-sm text-brand-red font-medium shrink-0">{c.n}</span>
                      <div>
                        <h3 className="font-display font-semibold text-slate-900 text-lg sm:text-xl">{c.title}</h3>
                        <p className="mt-3 text-sm sm:text-base text-slate-600 leading-relaxed">{c.text}</p>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.1}>
              <div className="mt-12 border-l-2 border-brand-red pl-7 py-1" data-testid="about-closing">
                <p className="font-display font-semibold text-slate-900 text-xl sm:text-2xl leading-snug">
                  The right people. The right opportunities. <span className="text-brand-red">Progress that works both ways.</span>
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.16}>
              <Link to="/contact" data-testid="about-cta"
                className="group mt-10 inline-flex items-center gap-2 bg-brand-red hover:bg-brand-dark text-white font-medium text-sm px-8 py-4 rounded-full transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_44px_-12px_rgba(181,51,49,0.6)]">
                Start the Conversation
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
